// sessionStorage-backed state for the post-click-out follow-up popup
// (docs/deal-signal-design.md §6). Deliberately client-only, no server round
// trip - the whole point of the tab-focus trigger is a same-session moment,
// so there's nothing here that needs to survive a closed tab.

const PENDING_CLICK_KEY = 'dealFollowup.pendingClick';
const HANDLED_ASINS_KEY = 'dealFollowup.handledAsins';
const LAST_SHOWN_AT_KEY = 'dealFollowup.lastShownAt';

function readJSON(key, fallback) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    // Private browsing / storage disabled / corrupted value - degrade to "no
    // pending state" rather than throw, same as every other best-effort
    // stats path in this app.
    return fallback;
  }
}

function writeJSON(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore - see readJSON.
  }
}

/**
 * Records the most recently clicked Amazon link as the pending follow-up
 * target. Most-recent-click-wins by design - overwrites any earlier pending
 * entry rather than queuing multiple items (docs/deal-signal-design.md §6
 * "Multi-click handling"). domain/href are carried along so a later
 * "ordered" answer can trigger an immediate purchase alert (§9) without
 * having to reconstruct the affiliate link server-side.
 * @param {string} asin
 * @param {string} [itemName]
 * @param {string} [domain]
 * @param {string} [href]
 */
export function recordPendingDealClick(asin, itemName, domain, href) {
  if (!asin) return;
  writeJSON(PENDING_CLICK_KEY, {
    asin,
    itemName: itemName || null,
    domain: domain || null,
    href: href || null,
    clickedAt: Date.now(),
  });
}

/** @returns {{asin: string, itemName: string|null, domain: string|null, href: string|null, clickedAt: number}|null} */
export function getPendingDealClick() {
  return readJSON(PENDING_CLICK_KEY, null);
}

export function clearPendingDealClick() {
  try {
    sessionStorage.removeItem(PENDING_CLICK_KEY);
  } catch {
    // Ignore.
  }
}

/** @returns {boolean} Whether this ASIN was already shown (answered or dismissed) this session. */
export function isDealFollowupHandled(asin) {
  const handled = readJSON(HANDLED_ASINS_KEY, []);
  return handled.includes(asin);
}

/** Marks an ASIN as handled so it's never re-prompted this session, regardless of outcome. */
export function markDealFollowupHandled(asin) {
  const handled = readJSON(HANDLED_ASINS_KEY, []);
  if (!handled.includes(asin)) {
    writeJSON(HANDLED_ASINS_KEY, [...handled, asin]);
  }
}

/** @param {number} cooldownMs @returns {boolean} Whether enough time has passed since the popup was last shown. */
export function isDealFollowupCooldownElapsed(cooldownMs) {
  const lastShownAt = readJSON(LAST_SHOWN_AT_KEY, 0);
  return Date.now() - lastShownAt >= cooldownMs;
}

export function markDealFollowupShownNow() {
  writeJSON(LAST_SHOWN_AT_KEY, Date.now());
}
